import React, { useContext, useEffect, useState } from "react";

import {
  AppBar,
  Box,
  Button,
  IconButton,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  AccountCircle,
  CheckCircle,
  LockRounded,
  LogoutRounded,
  Menu as MenuIcon,
} from "@mui/icons-material";

import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import { api } from "@api/api";
import { CondicaoContext } from "../contexto";
import IdentidadeVisual from "../estilos/identidadeVisual";
import NotificationContainer from "./notifications/NotificationContainer";
import ControleFonte from "./ControleFonte";
import { ajustarTamanhoFonteAdmin } from "../estilos/global";

export default function AppBarComponent({
  toggleDrawer,
  darkMode,
  visualNovo = false,
  exibirUsuarioAdmin = false,
}) {
  const navigate = useNavigate();

  const { role, userId, setCondicao, setRole, loginSuccess } =
    useContext(CondicaoContext);

  const [showLoginSuccess, setShowLoginSuccess] = useState(false);

  // LOGIN REALIZADO
  useEffect(() => {
    if (!loginSuccess) {
      return undefined;
    }

    setShowLoginSuccess(true);

    const timer = setTimeout(() => {
      setShowLoginSuccess(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, [loginSuccess]);

  // PERFIL DO ADMINISTRADOR
  const handlePerfil = () => {
    navigate("/admin/perfil");
  };

  // LOGOUT
  const handleLogout = async () => {
    try {
      await axios.post(
        api.logout(),
        {},
        {
          withCredentials: true,
        },
      );

      setCondicao(true);
      setRole(undefined);
      navigate("/login");
    } catch (error) {
      console.error("Erro ao fazer logout:", error);
    }
  };

  // VISUAL
  const Superficie = visualNovo ? IdentidadeVisual : Box;

  const corAcoes = darkMode ? "#d8ebff" : "#0086f5";

  // PAINEL INICIAL DE CADA PERFIL
  const rotaPainel =
    {
      admin: "/admin/painel",
      analyst: "/analista/painel",
      superintendent: "/admin",
      user: "/home/painel",
    }[role] ?? null;

  const nomePerfilBloqueado = {
    analyst: "Analista",
    superintendent: "Superintendência",
    user: "Escola",
  }[role];

  return (
    <AppBar
      position="relative"
      sx={{
        width: visualNovo ? "calc(100% - 8px)" : "100%",
        m: visualNovo ? 0.5 : 0,
        flexShrink: 0,

        background: visualNovo
          ? "transparent"
          : darkMode
            ? "linear-gradient(135deg, #0a1929, #1e3a5a)"
            : "linear-gradient(135deg, #1976d2, #64b5f6)",

        borderRadius: visualNovo ? "6px" : "0 0 20px 20px",

        boxShadow: visualNovo
          ? "0 2px 8px rgba(23, 92, 158, 0.16)"
          : "0 4px 20px rgba(25, 118, 210, 0.35)",
      }}
    >
      <Superficie
        {...(visualNovo
          ? {
              escuro: darkMode,
              sx: {
                borderRadius: "inherit",
              },
            }
          : {})}
      >
        <Toolbar
          sx={{
            minHeight: 64,

            px: visualNovo
              ? {
                  xs: 1,
                  sm: 2,
                }
              : undefined,

            gap: visualNovo
              ? {
                  xs: 1,
                  sm: 2,
                }
              : 2,
          }}
        >
          {/* MENU */}
          <IconButton
            edge={visualNovo ? false : "start"}
            color="inherit"
            onClick={toggleDrawer}
            aria-label="Abrir ou recolher menu"
            sx={{
              flexShrink: 0,
              transition: "background-color 120ms ease",

              "&:hover": {
                bgcolor: visualNovo ? "rgba(255,255,255,0.14)" : undefined,
              },
            }}
          >
            <MenuIcon sx={visualNovo ? { fontSize: 26 } : undefined} />
          </IconButton>

          {/* IDENTIDADE SEAR */}
          <Box
            sx={
              visualNovo
                ? {
                    display: "flex",
                    alignItems: "center",
                    flexGrow: 1,
                    minWidth: 0,

                    gap: {
                      xs: 1.5,
                      sm: 2,
                    },

                    borderLeft: "1px solid rgba(255,255,255,0.55)",

                    pl: {
                      xs: 1.5,
                      sm: 2,
                    },
                  }
                : {
                    flexGrow: 1,
                    textAlign: "center",
                  }
            }
          >
            <Typography
              component={rotaPainel ? "button" : "span"}
              type={rotaPainel ? "button" : undefined}
              onClick={rotaPainel ? () => navigate(rotaPainel) : undefined}
              aria-label={rotaPainel ? "Ir ao painel inicial" : undefined}
              sx={{
                display: "inline-block",
                flexShrink: 0,
                color: "#ffffff",
                whiteSpace: "nowrap",

                fontSize: visualNovo
                  ? {
                      xs: ajustarTamanhoFonteAdmin("1.4rem"),
                      sm: ajustarTamanhoFonteAdmin("1.75rem"),
                    }
                  : ajustarTamanhoFonteAdmin("1.25rem"),

                fontWeight: 800,
                letterSpacing: visualNovo ? "-0.04em" : "0.04em",
                lineHeight: 1,

                p: 0,
                border: 0,
                background: "transparent",
                fontFamily: "inherit",

                cursor: rotaPainel ? "pointer" : "default",

                "&:focus-visible": {
                  outline: "2px solid #ffffff",
                  outlineOffset: 4,
                  borderRadius: 0.5,
                },
              }}
            >
              SEAR
            </Typography>

            {visualNovo && (
              <Box
                sx={{
                  display: {
                    xs: "none",
                    sm: "block",
                  },

                  minWidth: 0,
                  borderLeft: "1px solid rgba(255,255,255,0.55)",
                  pl: 1.5,
                  color: "#ffffff",
                }}
              >
                <Typography
                  sx={{
                    fontSize: ajustarTamanhoFonteAdmin("0.95rem"),
                    fontWeight: 400,
                    lineHeight: 1.4,
                  }}
                >
                  Sistema Eletrônico
                  <br />
                  de Atendimento Remoto
                </Typography>
              </Box>
            )}
          </Box>

          {/* AÇÕES DO CABEÇALHO */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
              gap: visualNovo ? 1.5 : 0,

              ...(visualNovo && {
                color: corAcoes,

                bgcolor: {
                  xs: darkMode ? "#1c4265" : "#e1effd",
                  md: "transparent",
                },

                borderRadius: 1,
              }),
            }}
          >
            {/* NOTIFICAÇÕES */}
            {visualNovo &&
              ["admin", "superintendent", "user"].includes(role) && (
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    pr: 1,
                    borderRight: "1px solid",
                    borderColor: darkMode
                      ? "rgba(216,235,255,0.3)"
                      : "rgba(0,134,245,0.3)",
                  }}
                >
                  <NotificationContainer
                    key={`${role}:${userId}`}
                    perfil={role}
                  />
                </Box>
              )}

            {/* IDENTIFICAÇÃO DO ADMINISTRADOR */}
            {visualNovo && exibirUsuarioAdmin && (
              <Button
                type="button"
                color="inherit"
                onClick={handlePerfil}
                aria-label="Abrir meu perfil"
                startIcon={
                  <AccountCircle
                    aria-hidden="true"
                    sx={{
                      fontSize: "28px !important",
                    }}
                  />
                }
                sx={{
                  display: {
                    xs: "none",
                    md: "flex",
                  },

                  alignItems: "center",
                  minWidth: 0,
                  px: 0.75,
                  py: 0.5,
                  borderRadius: 1,
                  color: "inherit",

                  fontSize: ajustarTamanhoFonteAdmin("0.9rem"),
                  fontWeight: 500,
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  cursor: "pointer",

                  "& .MuiButton-startIcon": {
                    mr: 0.75,
                  },

                  "&:hover": {
                    bgcolor: darkMode
                      ? "rgba(216,235,255,0.08)"
                      : "rgba(0,134,245,0.08)",
                  },
                }}
              >
                Administrador
              </Button>
            )}

            {/* NOTIFICAÇÕES DO ANALISTA */}
            {visualNovo && role === "analyst" && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  px: 1,

                  borderLeft: "1px solid",
                  borderRight: "1px solid",

                  borderColor: darkMode
                    ? "rgba(216,235,255,0.3)"
                    : "rgba(0,134,245,0.3)",
                }}
              >
                <NotificationContainer
                  key={`${role}:${userId}`}
                  perfil={role}
                />
              </Box>
            )}

            {/* USUÁRIO — APENAS VISUAL */}
            {visualNovo && nomePerfilBloqueado && (
              <Tooltip title="Perfil em desenvolvimento" arrow>
                <Box
                  component="span"
                  sx={{
                    display: {
                      xs: "none",
                      md: "inline-flex",
                    },
                  }}
                >
                  <Button
                    type="button"
                    disabled
                    aria-label={`Perfil ${nomePerfilBloqueado} em desenvolvimento`}
                    startIcon={
                      <AccountCircle
                        aria-hidden="true"
                        sx={{ fontSize: "28px !important" }}
                      />
                    }
                    endIcon={
                      <LockRounded
                        aria-hidden="true"
                        sx={{ fontSize: "14px !important" }}
                      />
                    }
                    sx={{
                      minWidth: 0,
                      px: 0.75,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: ajustarTamanhoFonteAdmin("0.9rem"),
                      fontWeight: 500,
                      textTransform: "none",
                      whiteSpace: "nowrap",

                      "&.Mui-disabled": {
                        color: "text.disabled",
                      },

                      "& .MuiButton-startIcon": {
                        mr: 0.75,
                      },

                      "& .MuiButton-endIcon": {
                        ml: 0.75,
                      },
                    }}
                  >
                    {nomePerfilBloqueado}
                  </Button>
                </Box>
              </Tooltip>
            )}

            {/* CONTROLE GLOBAL DE FONTES */}
            <ControleFonte />

            {/* LOGOUT */}
            <Box
              sx={
                visualNovo && (exibirUsuarioAdmin || role === "analyst")
                  ? {
                      borderLeft: {
                        xs: "none",

                        md: darkMode
                          ? "1px solid rgba(216,235,255,0.3)"
                          : "1px solid rgba(0,134,245,0.2)",
                      },

                      pl: {
                        xs: 0,
                        md: 1.5,
                      },
                    }
                  : {}
              }
            >
              <Button
                color="inherit"
                onClick={handleLogout}
                startIcon={visualNovo ? <LogoutRounded /> : undefined}
                sx={{
                  textTransform: visualNovo ? "none" : undefined,

                  borderRadius: 1,
                  minWidth: visualNovo ? 0 : undefined,

                  px: visualNovo ? 1 : 2,

                  fontSize: visualNovo
                    ? ajustarTamanhoFonteAdmin("0.9rem")
                    : undefined,

                  whiteSpace: "nowrap",

                  "&:hover": {
                    bgcolor: visualNovo
                      ? "rgba(0,134,245,0.08)"
                      : "rgba(255,255,255,0.16)",
                  },
                }}
              >
                {visualNovo ? "Sair" : "Logout"}
              </Button>
            </Box>
          </Box>
        </Toolbar>

        {/* MENSAGEM DE LOGIN */}
        <AnimatePresence>
          {(!visualNovo || !exibirUsuarioAdmin) && showLoginSuccess && (
            <motion.div
              initial={{
                opacity: 0,
                y: -12,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              exit={{
                opacity: 0,
                y: -12,
              }}
              style={{
                position: "absolute",
                top: "100%",
                right: 16,
                zIndex: 2,

                display: "flex",
                alignItems: "center",
                gap: 8,

                padding: "8px 14px",

                color: "#ffffff",
                background: "#2e7d32",

                borderRadius: 8,

                boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
              }}
            >
              <CheckCircle fontSize="small" />
              Login realizado com sucesso!
            </motion.div>
          )}
        </AnimatePresence>
      </Superficie>
    </AppBar>
  );
}
